import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useWorkspace, useWorkspaceCan } from "./WorkspaceContext";

type Playlist = {
  id: string;
  name: string;
  description: string | null;
  visibility: string;
  type: string;
  // #2622: this playlist is delivered through a public link AND still holds
  // gated videos (NDA-tier / campaign-assigned) that a public viewer can never
  // unlock. They are hidden from viewers; the admin should fix the pack.
  gatedTiers?: string[];
};

// Name the offending tier so the chip is accurate on a campaign-assigned-only
// pack (board #2622 copy is "NDA-tier hidden" where NDA-tier is involved).
function gatedChipLabel(tiers: string[]): string {
  if (tiers.includes("nda_signed")) return "NDA-tier hidden";
  return "Campaign-assigned hidden";
}

type VideoOption = { id: string; title: string };

export function PlaylistsTab() {
  const { campaignId } = useWorkspace();
  // #2504-2506: playlist authoring for super_admin / owning campaign_owner / ae.
  const { managePlaylists } = useWorkspaceCan();
  const qc = useQueryClient();
  const base = `/api/admin/playlists`;
  const [createOpen, setCreateOpen] = useState(false);

  const { data } = useQuery({
    queryKey: ["workspace-playlists", campaignId],
    queryFn: () => api.get<{ items: Playlist[] }>(`${base}?campaignId=${campaignId}`),
  });
  const items = data?.items ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-head text-lg font-semibold">Playlists / Templates</h2>
          <p className="text-xs text-muted-foreground">
            Reusable templates for this campaign — e.g. New Associate Onboarding, Investor Update.
          </p>
        </div>
        {managePlaylists && <Button onClick={() => setCreateOpen(true)}>New Playlist</Button>}
      </div>

      <Card>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-admin-rule">
              <th className="p-3 font-medium">Name</th>
              <th className="p-3 font-medium">Type</th>
              <th className="p-3 font-medium">Visibility</th>
              <th className="p-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-b border-admin-rule">
                <td className="p-3">
                  <div className="font-medium">
                    {p.name}
                    {(p.gatedTiers?.length ?? 0) > 0 && (
                      <Badge
                        variant="danger"
                        className="ml-2"
                        title="This playlist is delivered through a public link but contains gated videos (NDA-tier or campaign-assigned). Public viewers can never unlock them, so they are hidden from the pack. Remove those videos, or change this to an NDA-gated/private flow."
                      >
                        {gatedChipLabel(p.gatedTiers ?? [])}
                      </Badge>
                    )}
                  </div>
                  {p.description && (
                    <div className="text-xs text-muted-foreground line-clamp-1">{p.description}</div>
                  )}
                </td>
                <td className="p-3"><Badge variant="outline">{p.type}</Badge></td>
                <td className="p-3"><Badge variant="muted">{p.visibility}</Badge></td>
                <td className="p-3 text-right">
                  {managePlaylists && (
                    <Link to={`/admin/playlists/${p.id}`} className="text-electric text-xs hover:underline">
                      Edit videos →
                    </Link>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={4} className="p-6 text-center text-xs text-muted-foreground">
                  No playlists in this campaign yet. Create one from this campaign's approved media.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>

      <CreatePlaylistDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        campaignId={campaignId}
        onSaved={() => qc.invalidateQueries({ queryKey: ["workspace-playlists", campaignId] })}
      />
    </div>
  );
}

function CreatePlaylistDialog({
  open,
  onOpenChange,
  campaignId,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  campaignId: string;
  onSaved: () => void;
}) {
  const [name, setName] = useState("");
  const [selected, setSelected] = useState<string[]>([]);

  // Only this campaign's APPROVED videos are eligible (mirrors the builder).
  const { data } = useQuery({
    queryKey: ["workspace-playlist-videos", campaignId],
    queryFn: () =>
      api.get<{ items: VideoOption[] }>(
        `/api/admin/videos?campaignId=${campaignId}&status=approved`,
      ),
    enabled: open,
  });
  const videos = data?.items ?? [];

  const toggle = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const save = useMutation({
    mutationFn: () =>
      api.post("/api/admin/playlists", {
        name,
        type: "template",
        campaignId,
        items: selected.map((videoId, i) => ({ videoId, sortOrder: i })),
      }),
    onSuccess: () => {
      toast.success("Playlist created");
      onSaved();
      setName("");
      setSelected([]);
      onOpenChange(false);
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Failed"),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New playlist</DialogTitle>
          <DialogDescription>
            A campaign-scoped template built from this campaign's approved media.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className="space-y-3">
            <Input placeholder="Playlist name" value={name} onChange={(e) => setName(e.target.value)} />
            <div className="border border-admin-rule rounded-md max-h-64 overflow-y-auto divide-y divide-admin-rule">
              {videos.length === 0 && (
                <p className="text-xs text-muted-foreground p-3">
                  No approved videos in this campaign yet — add and approve media first.
                </p>
              )}
              {videos.map((v) => (
                <label key={v.id} className="flex items-center gap-2 p-2 text-sm hover:bg-foreground/5 cursor-pointer">
                  <input type="checkbox" checked={selected.includes(v.id)} onChange={() => toggle(v.id)} />
                  <span className="line-clamp-1">{v.title}</span>
                </label>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground">{selected.length} selected</p>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={!name || selected.length === 0 || save.isPending}>
            {save.isPending ? "Saving…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
